
import { useState, useCallback, useEffect, useMemo } from 'react';
import { api } from '@/lib/api';
import { Button, Modal } from '@/components/ui';
import { PrayerRequest } from '@/types';
import { PrayerType } from '@/types';
import { formatFullName, formatGender } from '@/lib/format';
import { Badge } from '@/components/ui';
import { cn } from '@/lib/utils';
import { VideoRecorderModal } from './VideoRecorderModal';
import { AudioRecorderModal } from './AudioRecorderModal';

const MAX_RECORDING_SECONDS = 60;
const MAX_RECORDING_BYTES = 20 * 1024 * 1024;
const MAX_TEXT_CHARS = 1000;

type ResponseKind = 'video' | 'audio' | 'text';

function maskEmail(email: string): string {
  if (!email || !email.includes('@')) return email;
  const [local, domain] = email.split('@');
  if (local == null || domain == null) return email;
  if (local.length <= 2) return `${local[0]}***@${domain}`;
  return `${local[0]}***${local[local.length - 1]}@${domain}`;
}

function maskPhone(phone: string): string {
  const digits = phone.replace(/\D/g, '');
  if (digits.length < 4) return '***';
  return `***-${digits.slice(-4)}`;
}

export interface RespondModalProps {
  prayer: PrayerRequest | null;
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  canRespondVideo?: boolean;
  canRespondAudio?: boolean;
}

export function RespondModal({
  prayer,
  isOpen,
  onClose,
  onSuccess,
  canRespondVideo = false,
  canRespondAudio = false,
}: RespondModalProps) {
  const defaultKind = useMemo<ResponseKind>(() => {
    if (canRespondVideo) return 'video';
    if (canRespondAudio) return 'audio';
    return 'text';
  }, [canRespondVideo, canRespondAudio]);

  const [responseKind, setResponseKind] = useState<ResponseKind>(defaultKind);
  const [responseText, setResponseText] = useState('');
  const [recordedBlob, setRecordedBlob] = useState<Blob | null>(null);
  const [recordedDurationSeconds, setRecordedDurationSeconds] = useState<number | null>(null);
  const [showVideoRecorder, setShowVideoRecorder] = useState(false);
  const [showAudioRecorder, setShowAudioRecorder] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [deliveryMethod, setDeliveryMethod] = useState<'email' | 'sms'>('email');

  const resetState = useCallback(() => {
    setResponseKind(defaultKind);
    setResponseText('');
    setRecordedBlob(null);
    setRecordedDurationSeconds(null);
    setShowVideoRecorder(false);
    setShowAudioRecorder(false);
    setSubmitError(null);
  }, [defaultKind]);

  const handleClose = useCallback(() => {
    resetState();
    onClose();
  }, [resetState, onClose]);

  useEffect(() => {
    if (isOpen && prayer) {
      resetState();
      const hasEmail = !!(prayer.email && prayer.email.trim());
      const hasPhone = !!(prayer.phone && prayer.phone.trim());
      if (hasEmail && hasPhone) setDeliveryMethod('sms');
      else if (hasPhone) setDeliveryMethod('sms');
      else if (hasEmail) setDeliveryMethod('email');
    }
  }, [isOpen, prayer?.id, resetState]);

  const handleKindChange = useCallback((kind: ResponseKind) => {
    setResponseKind(kind);
    setSubmitError(null);
    if (kind === 'text') {
      setRecordedBlob(null);
      setRecordedDurationSeconds(null);
      setShowVideoRecorder(false);
      setShowAudioRecorder(false);
    } else {
      setResponseText('');
      setRecordedBlob(null);
      setRecordedDurationSeconds(null);
      setShowVideoRecorder(false);
      setShowAudioRecorder(false);
    }
  }, []);

  const handleRecordingComplete = useCallback((blob: Blob, durationSeconds: number) => {
    setRecordedBlob(blob);
    setRecordedDurationSeconds(durationSeconds);
    setShowVideoRecorder(false);
    setShowAudioRecorder(false);
  }, []);

  const handleReRecord = useCallback(() => {
    setRecordedBlob(null);
    setRecordedDurationSeconds(null);
    if (responseKind === 'video') setShowVideoRecorder(true);
    if (responseKind === 'audio') setShowAudioRecorder(true);
  }, [responseKind]);

  const handleViewRecording = useCallback(() => {
    if (!recordedBlob) return;
    const url = URL.createObjectURL(recordedBlob);
    window.open(url, '_blank', 'noopener');
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  }, [recordedBlob]);

  const uploadRecordedResponse = useCallback(async () => {
    if (!prayer || !recordedBlob) return false;
    const endpoint =
      responseKind === 'audio'
        ? `/prayer-requests/${prayer.id}/responses/initiate-audio`
        : `/prayer-requests/${prayer.id}/responses/initiate-video`;

    const initRes = await api.post<{
      uploadUrl: string;
      responseId: string;
      storageKey: string;
    }>(endpoint, {
      deliveryMethod,
      contentType: recordedBlob.type,
    });
    if (!initRes.data?.uploadUrl || !initRes.data?.responseId) {
      setSubmitError(initRes.error || 'Failed to get upload link');
      return false;
    }
    const { uploadUrl, responseId } = initRes.data;
    const putRes = await fetch(uploadUrl, {
      method: 'PUT',
      body: recordedBlob,
      headers: { 'Content-Type': recordedBlob.type },
    });
    if (!putRes.ok) {
      setSubmitError('Upload failed. Please try again.');
      return false;
    }
    const confirmRes = await api.post(
      `/prayer-requests/${prayer.id}/responses/${responseId}/confirm-upload`,
      { deliveryMethod },
      { auth: true }
    );
    if (confirmRes.data) {
      handleClose();
      onSuccess();
      return true;
    }
    setSubmitError(confirmRes.error || 'Failed to complete response');
    return false;
  }, [prayer, responseKind, deliveryMethod, recordedBlob, handleClose, onSuccess]);

  const handleSubmitResponse = useCallback(async () => {
    if (!prayer) return;

    if (responseKind === 'text') {
      const trimmed = responseText.trim();
      if (!trimmed) {
        setSubmitError('Please enter your response.');
        return;
      }
      setSubmitting(true);
      setSubmitError(null);
      try {
        const form = new FormData();
        form.append('response_type', 'text');
        form.append('text_body', trimmed);
        form.append('delivery_method', deliveryMethod);
        const res = await api.post(`/prayer-requests/${prayer.id}/responses`, form, {
          auth: true,
        });
        if (res.data) {
          handleClose();
          onSuccess();
        } else {
          setSubmitError(res.error || 'Failed to send response');
        }
      } finally {
        setSubmitting(false);
      }
      return;
    }

    if (!recordedBlob) {
      setSubmitError(`Please record a ${responseKind} response.`);
      return;
    }
    if (recordedBlob.size > MAX_RECORDING_BYTES) {
      setSubmitError(`Recording is too large (max ${MAX_RECORDING_BYTES / (1024 * 1024)} MB). Try recording again.`);
      return;
    }
    setSubmitting(true);
    setSubmitError(null);
    try {
      await uploadRecordedResponse();
    } finally {
      setSubmitting(false);
    }
  }, [
    prayer,
    responseKind,
    responseText,
    deliveryMethod,
    recordedBlob,
    handleClose,
    onSuccess,
    uploadRecordedResponse,
  ]);

  if (!prayer) return null;

  const hasEmail = !!(prayer.email && prayer.email.trim());
  const hasPhone = !!(prayer.phone && prayer.phone.trim());
  const canNotify = hasEmail || hasPhone;
  const displayName = formatFullName(prayer.firstName, prayer.lastName) || 'Anonymous';
  const genderLabel = formatGender(prayer.gender);
  const isPrayer = prayer.type === PrayerType.PRAYER_REQUEST || prayer.type === PrayerType.PRAYER;
  const typeLabel = isPrayer ? 'Prayer Request' : 'Answered Prayer';

  const canSendRecorded = !!recordedBlob && canNotify;
  const canSendText = responseText.trim().length > 0 && canNotify;
  const canSend =
    responseKind === 'text' ? canSendText : canSendRecorded;

  const kindOptions = ([
    { id: 'text' as const, label: 'Text', show: true },
    { id: 'audio' as const, label: 'Audio', show: canRespondAudio },
    { id: 'video' as const, label: 'Video', show: canRespondVideo },
  ] satisfies { id: ResponseKind; label: string; show: boolean }[]).filter((o) => o.show);

  return (
    <>
      <Modal isOpen={isOpen} onClose={handleClose} title="Send a response" size="lg">
        <div className="space-y-4">
          <div className="rounded-lg border border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-800/50 p-4 space-y-2">
            <div className="flex flex-wrap items-center gap-2">
              <Badge variant={isPrayer ? 'sky' : 'amber'}>{typeLabel}</Badge>
              <span className="text-sm font-medium text-slate-900 dark:text-white">{displayName}</span>
              {genderLabel && (
                <span className="text-xs text-slate-500 dark:text-slate-400">• {genderLabel}</span>
              )}
              {prayer.location && (
                <span className="text-xs text-slate-500 dark:text-slate-400">• {prayer.location}</span>
              )}
            </div>
            {(hasEmail || hasPhone) && (
              <div className="flex flex-wrap gap-3 text-xs text-slate-600 dark:text-slate-400">
                {hasEmail && <span>{maskEmail(prayer.email!.trim())}</span>}
                {hasPhone && <span>{maskPhone(prayer.phone!.trim())}</span>}
              </div>
            )}
            <div className="rounded-md bg-white dark:bg-slate-800 p-3 border border-slate-100 dark:border-slate-700">
              <p className="text-sm text-slate-700 dark:text-slate-300 whitespace-pre-wrap">{prayer.body}</p>
            </div>
          </div>

          {kindOptions.length > 1 && (
            <div className="space-y-2">
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300">
                Response type
              </label>
              <div className="flex rounded-lg border border-slate-200 dark:border-slate-600 p-0.5 bg-slate-100 dark:bg-slate-800">
                {kindOptions.map((opt) => (
                  <button
                    key={opt.id}
                    type="button"
                    onClick={() => handleKindChange(opt.id)}
                    disabled={submitting}
                    className={cn(
                      'flex-1 py-2 text-sm font-medium rounded-md transition-colors min-h-[44px]',
                      responseKind === opt.id
                        ? 'bg-white dark:bg-slate-700 text-slate-900 dark:text-white shadow'
                        : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white'
                    )}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="space-y-3">
            {responseKind === 'video' || responseKind === 'audio' ? (
              <>
                <p className="text-sm text-slate-600 dark:text-slate-400">
                  Record up to {MAX_RECORDING_SECONDS} seconds
                  {responseKind === 'video' ? ' (portrait).' : '.'}
                </p>
                {!recordedBlob ? (
                  <Button
                    variant="secondary"
                    onClick={() =>
                      responseKind === 'video'
                        ? setShowVideoRecorder(true)
                        : setShowAudioRecorder(true)
                    }
                    disabled={submitting}
                    className="w-full sm:w-auto min-h-[44px]"
                  >
                    Record {responseKind === 'video' ? 'Video' : 'Audio'}
                  </Button>
                ) : (
                  <div className="flex flex-wrap items-center gap-3 rounded-lg border border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-800/50 p-4">
                    <span className="flex items-center gap-2 text-sm font-medium text-slate-900 dark:text-white">
                      <span className="flex h-6 w-6 items-center justify-center rounded-full bg-emerald-500 text-white" aria-hidden>
                        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                      </span>
                      {responseKind === 'video' ? 'Video' : 'Audio'} recorded
                      {recordedDurationSeconds != null && (
                        <span className="text-slate-600 dark:text-slate-400 font-normal">
                          ({recordedDurationSeconds}s)
                        </span>
                      )}
                    </span>
                    <Button variant="ghost" size="sm" onClick={handleViewRecording} disabled={submitting}>
                      View
                    </Button>
                    <Button variant="ghost" size="sm" onClick={handleReRecord} disabled={submitting}>
                      Record Again
                    </Button>
                  </div>
                )}
              </>
            ) : (
              <div className="space-y-2">
                <label htmlFor="respond-text" className="block text-sm font-medium text-slate-700 dark:text-slate-300">
                  Your message
                </label>
                <textarea
                  id="respond-text"
                  value={responseText}
                  onChange={(e) => setResponseText(e.target.value.slice(0, MAX_TEXT_CHARS))}
                  rows={6}
                  maxLength={MAX_TEXT_CHARS}
                  disabled={submitting}
                  placeholder="Write your response…"
                  className="w-full rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 px-3 py-2 text-sm text-slate-900 dark:text-white placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-primary-500 min-h-[120px]"
                />
                <p className="text-xs text-slate-500 dark:text-slate-400 text-right">
                  {responseText.length}/{MAX_TEXT_CHARS}
                </p>
              </div>
            )}

            {canNotify && (
              <div className="space-y-2">
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300">
                  Send response via
                </label>
                {hasEmail && hasPhone ? (
                  <div className="flex rounded-lg border border-slate-200 dark:border-slate-600 p-0.5 bg-slate-100 dark:bg-slate-800">
                    <button
                      type="button"
                      onClick={() => setDeliveryMethod('email')}
                      className={cn(
                        'flex-1 py-2 text-sm font-medium rounded-md transition-colors min-h-[44px]',
                        deliveryMethod === 'email'
                          ? 'bg-white dark:bg-slate-700 text-slate-900 dark:text-white shadow'
                          : 'text-slate-600 dark:text-slate-400'
                      )}
                    >
                      Email
                    </button>
                    <button
                      type="button"
                      onClick={() => setDeliveryMethod('sms')}
                      className={cn(
                        'flex-1 py-2 text-sm font-medium rounded-md transition-colors min-h-[44px]',
                        deliveryMethod === 'sms'
                          ? 'bg-white dark:bg-slate-700 text-slate-900 dark:text-white shadow'
                          : 'text-slate-600 dark:text-slate-400'
                      )}
                    >
                      Text
                    </button>
                  </div>
                ) : (
                  <p className="text-sm text-slate-600 dark:text-slate-400">
                    {hasEmail ? 'Will be sent via email' : 'Will be sent via text'}
                  </p>
                )}
              </div>
            )}
            {!canNotify && (
              <p className="text-sm text-amber-600 dark:text-amber-400">
                This submitter has no email or phone on file. They cannot be notified.
              </p>
            )}
          </div>

          {submitError && <p className="text-sm text-amber-600 dark:text-amber-400">{submitError}</p>}
          <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2">
            <Button variant="secondary" onClick={handleClose} className="min-h-[44px] sm:min-h-0">
              Cancel
            </Button>
            <Button onClick={handleSubmitResponse} disabled={submitting || !canSend} className="min-h-[44px] sm:min-h-0">
              {submitting ? 'Sending...' : 'Send response'}
            </Button>
          </div>
        </div>
      </Modal>

      <VideoRecorderModal
        isOpen={showVideoRecorder}
        onClose={() => setShowVideoRecorder(false)}
        onComplete={handleRecordingComplete}
        displayName={displayName}
        prayerBody={prayer.body}
        maxSeconds={MAX_RECORDING_SECONDS}
      />
      <AudioRecorderModal
        isOpen={showAudioRecorder}
        onClose={() => setShowAudioRecorder(false)}
        onComplete={handleRecordingComplete}
        displayName={displayName}
        prayerBody={prayer.body}
        maxSeconds={MAX_RECORDING_SECONDS}
      />
    </>
  );
}
